import { z } from 'zod';
import { CampaignMessages } from './delivery.ts';
import { Conversions } from './conversions.ts';
import { DeliveryControls, MessageExemptions } from './delivery-controls.ts';
import { ActionBasedDelivery } from './triggers.ts';
import { MESSAGE_CHANNELS, type AudienceReach } from './audience.ts';
import type { ApprovalState } from './approvals.ts';
import type { DependentView } from './segments.ts';

/**
 * Campaigns (BB-96, CMP-001/CMP-014): a campaign runs on an immutable published revision; edits go to one working draft
 * revision until it is published. `idle` is not stored: an active campaign that sent nothing for the environment's
 * idle threshold (default 30 days) is listed as idle.
 */
export const CAMPAIGN_STATUSES = ['draft', 'scheduled', 'active', 'paused', 'stopped', 'completed', 'archived'] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];
export const CAMPAIGN_DELIVERY_TYPES = ['scheduled', 'action_based', 'api_triggered'] as const;
export const CAMPAIGN_CATEGORIES = ['marketing', 'transactional'] as const;
export const CAMPAIGN_SORTS = ['updated', 'created', 'name'] as const;
/** Actions of the row menu and the editor; which ones a status allows comes with every campaign (`actions`). */
export const CAMPAIGN_ACTIONS = ['publish', 'pause', 'resume', 'stop', 'archive', 'restore', 'duplicate'] as const;
export type CampaignAction = (typeof CAMPAIGN_ACTIONS)[number];
export const CAMPAIGN_IDLE_DAYS_DEFAULT = 30;

const Name = z.string().trim().min(1).max(200);
const Description = z.string().trim().max(1000).nullable();
const Channels = z.array(z.enum(MESSAGE_CHANNELS)).min(1).max(MESSAGE_CHANNELS.length);

/** List filters: no `status` lists everything but archived; `idle` lists active campaigns past the idle threshold. */
export const CampaignListQuery = z.object({
  query: z.string().trim().max(200).optional(),
  status: z.enum([...CAMPAIGN_STATUSES, 'idle', 'all']).optional(),
  deliveryType: z.enum(CAMPAIGN_DELIVERY_TYPES).optional(),
  channel: z.enum(MESSAGE_CHANNELS).optional(),
  tagId: z.uuid().optional(),
  createdBy: z.uuid().optional(),
  sort: z.enum(CAMPAIGN_SORTS).default('updated'),
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type CampaignListQuery = z.infer<typeof CampaignListQuery>;

const LocalDate = z.iso.date();
const LocalTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:mm');

/**
 * When a scheduled campaign sends (BB-99, CMP-003/004). Times are wall-clock: in the environment's time zone, or in
 * each user's own zone with `localTime` (sent in waves, so it must be planned at least 24 hours ahead). Monthly on a
 * day the month lacks sends on its last day; the end date is inclusive.
 */
export const ScheduledDelivery = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('immediate') }),
  z.strictObject({ kind: z.literal('at'), date: LocalDate, time: LocalTime, localTime: z.boolean().default(false) }),
  z
    .strictObject({
      kind: z.literal('recurring'),
      every: z.enum(['day', 'week', 'month']),
      interval: z.number().int().min(1).max(12).default(1),
      /** ISO weekdays (1 = Monday) for `week`. */
      daysOfWeek: z.array(z.number().int().min(1).max(7)).min(1).max(7).optional(),
      dayOfMonth: z.number().int().min(1).max(31).optional(),
      time: LocalTime,
      localTime: z.boolean().default(false),
      startDate: LocalDate,
      endDate: LocalDate.optional(),
    })
    .refine((s) => s.every !== 'week' || Boolean(s.daysOfWeek), { message: 'weekly needs daysOfWeek', path: ['daysOfWeek'] })
    .refine((s) => s.every !== 'month' || s.dayOfMonth !== undefined, { message: 'monthly needs dayOfMonth', path: ['dayOfMonth'] })
    .refine((s) => !s.endDate || s.endDate >= s.startDate, { message: 'endDate is before startDate', path: ['endDate'] }),
]);
export type ScheduledDelivery = z.infer<typeof ScheduledDelivery>;

/** A planned send of a scheduled campaign («Next Send Time»); local-time sends start with the earliest time zone. */
export interface NextRunView {
  runKey: string;
  /** First wave (local time) or the send itself. */
  at: string;
  localTime: boolean;
}

/**
 * Draft content. Its parts are owned by their tickets: messages BB-98, schedule BB-99…BB-101, delivery controls and
 * exemptions BB-102, conversions BB-103 (locked once the campaign has been launched).
 */
const contentParts = {
  /** Channels, variants and control (BB-98); `[]` until the messages are composed. */
  messages: z.union([z.tuple([]), CampaignMessages]),
  schedule: z.looseObject({ startAt: z.iso.datetime().nullable().optional(), endAt: z.iso.datetime().nullable().optional(), scheduled: ScheduledDelivery.optional(), actionBased: ActionBasedDelivery.optional() }),
  audience: z.unknown().nullable(),
  conversions: Conversions,
  deliveryControls: DeliveryControls,
  exemptions: MessageExemptions,
};

export const CampaignContent = z.strictObject({
  messages: contentParts.messages.default([]),
  schedule: contentParts.schedule.default({}),
  audience: contentParts.audience.default(null),
  conversions: contentParts.conversions.default([]),
  deliveryControls: contentParts.deliveryControls.default({}),
  exemptions: contentParts.exemptions.default({}),
});
export type CampaignContent = z.infer<typeof CampaignContent>;

/**
 * Parts of the content a draft edit replaces; a part left out stays as it is. (`CampaignContent.partial()` would fill
 * the missing parts with their defaults and wipe them.)
 */
export const CampaignContentPatch = z.strictObject({
  messages: contentParts.messages.optional(),
  schedule: contentParts.schedule.optional(),
  audience: contentParts.audience.optional(),
  conversions: contentParts.conversions.optional(),
  deliveryControls: contentParts.deliveryControls.optional(),
  exemptions: contentParts.exemptions.optional(),
});
export type CampaignContentPatch = z.infer<typeof CampaignContentPatch>;

export const CreateCampaignBody = z.strictObject({
  name: Name,
  description: Description.optional(),
  deliveryType: z.enum(CAMPAIGN_DELIVERY_TYPES),
  category: z.enum(CAMPAIGN_CATEGORIES).default('marketing'),
  channels: Channels,
  content: CampaignContentPatch.optional(),
});
export type CreateCampaignBody = z.infer<typeof CreateCampaignBody>;

/** Edits the working draft; `draftVersion` guards concurrent edits (412 `revision_conflict`). */
export const UpdateCampaignDraftBody = z
  .strictObject({
    draftVersion: z.number().int().min(0),
    name: Name.optional(),
    description: Description.optional(),
    channels: Channels.optional(),
    content: CampaignContentPatch.optional(),
  })
  .refine((b) => b.name !== undefined || b.description !== undefined || b.channels !== undefined || b.content !== undefined, 'nothing to change');
export type UpdateCampaignDraftBody = z.infer<typeof UpdateCampaignDraftBody>;

/** Steps of the campaign wizard (BB-97, CMP-002); every validation issue names the step that fixes it. */
export const CAMPAIGN_WIZARD_STEPS = ['compose', 'schedule', 'target', 'conversions', 'review'] as const;
export type CampaignWizardStep = (typeof CAMPAIGN_WIZARD_STEPS)[number];

/** A problem of a draft: errors block the launch, warnings only inform. */
export interface CampaignDraftIssue {
  step: CampaignWizardStep;
  /** Dotted path into the draft content (`messages.variants.0.content.push_android.body`); `''` for the whole step. */
  path: string;
  severity: 'error' | 'warning';
  code: string;
  message: string;
}

/** `POST …/campaigns/:id/draft/validate`: parts sent in `content` (unsaved edits) are checked in place of the stored ones. */
export const ValidateCampaignDraftBody = z.strictObject({ content: CampaignContentPatch.optional() }).default({});
export type ValidateCampaignDraftBody = z.infer<typeof ValidateCampaignDraftBody>;

/**
 * The aggregated check of the wizard and of publish (BB-97): publishing refuses (422) while `valid` is false, with the
 * same error issues. `reach` is the audience estimate (BB-44); `null` when it cannot be estimated.
 */
export interface CampaignDraftValidation {
  valid: boolean;
  issues: CampaignDraftIssue[];
  reach: Pick<AudienceReach, 'mode' | 'count' | 'low' | 'high' | 'reachable'> | null;
}

/** Resuming a stopped campaign only runs future sends; the reason goes to the audit log (CMP-015). */
export const ResumeCampaignBody = z.strictObject({ reason: z.string().trim().min(3).max(500).optional() }).default({});
export type ResumeCampaignBody = z.infer<typeof ResumeCampaignBody>;

export const StopCampaignBody = z.strictObject({ reason: z.string().trim().max(500).optional() }).default({});
export type StopCampaignBody = z.infer<typeof StopCampaignBody>;

export interface CampaignPerson {
  id: string;
  email: string | null;
}

export interface CampaignSummary {
  id: string;
  apiId: string;
  name: string;
  description: string | null;
  status: CampaignStatus;
  /** Active, but nothing sent within the idle threshold. */
  idle: boolean;
  deliveryType: (typeof CAMPAIGN_DELIVERY_TYPES)[number];
  category: (typeof CAMPAIGN_CATEGORIES)[number];
  channels: Array<(typeof MESSAGE_CHANNELS)[number]>;
  tags: Array<{ id: string; name: string; color: string | null }>;
  /** Sends counted by the daily reporting aggregates (test sends excluded). */
  sent: number;
  /** Published revision number, `null` before the first publish. */
  publishedRevision: number | null;
  /** The draft holds changes that are not published yet. */
  hasDraftChanges: boolean;
  startAt: string | null;
  /** «Stop date» of the published schedule. */
  endAt: string | null;
  lastSentAt: string | null;
  firstPublishedAt: string | null;
  createdBy: CampaignPerson | null;
  updatedBy: CampaignPerson | null;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  actions: CampaignAction[];
}

export interface CampaignList {
  items: CampaignSummary[];
  nextCursor: string | null;
  /** Idle campaigns of the environment (for the banner), whatever the filters. */
  idleCount: number;
  idleDays: number;
}

export interface CampaignView extends CampaignSummary {
  /** What the next draft edit sends as `draftVersion`, also when there is no draft yet (an edit of a launched campaign). */
  draftVersion: number;
  /** `version` guards edits; `revision` is the draft's revision number (for diffs against the published one). */
  draft: { version: number; revision: number; content: CampaignContent } | null;
  /** Approval of the draft (BB-106). */
  approval: ApprovalState;
  published: { revision: number; content: CampaignContent; publishedAt: string; publishedBy: CampaignPerson | null } | null;
}

export interface CampaignRevisionView {
  revision: number;
  /** `false` for the working draft. */
  published: boolean;
  content: CampaignContent;
  contentHash: string;
  publishedAt: string | null;
  publishedBy: CampaignPerson | null;
  approvalState: string;
  createdAt: string;
}

export interface CampaignRevisionDiff {
  from: number;
  to: number;
  changes: Array<{ path: string; before?: unknown; after?: unknown }>;
}

export interface CampaignArchiveResult extends CampaignView {
  warnings: DependentView[];
}

/** Personal preferences of an employee (saved list columns and the like), one JSON value per key. */
export const PreferenceKey = z.string().regex(/^[a-z0-9][a-z0-9_.:-]{0,99}$/, 'lowercase letters, digits and _.:-, up to 100 chars');
export const PutPreferenceBody = z.strictObject({ value: z.unknown().refine((v) => v !== undefined && JSON.stringify(v).length <= 8192, 'at most 8 KB of JSON') });

/**
 * Content calendar (BB-108, CMP-014/EXT-005): planned sends of live campaigns and Canvases over a range of days in the
 * environment's time zone. Scheduled delivery gives one item per run (`scheduled_run`); action-based and API-triggered
 * delivery gives the period it can send in (`active_window`). An overlap is two runs of different objects on the same
 * day through a common channel.
 */
export const CALENDAR_OBJECT_TYPES = ['campaign', 'canvas'] as const;
export const CALENDAR_STATUSES = ['active', 'paused'] as const;
export const CALENDAR_MAX_DAYS = 62;
export const CalendarQuery = z
  .object({
    /** First and last day (inclusive), local dates of the environment. */
    from: LocalDate,
    to: LocalDate,
    objectType: z.enum(CALENDAR_OBJECT_TYPES).optional(),
    channel: z.enum(MESSAGE_CHANNELS).optional(),
    tagId: z.uuid().optional(),
    /** `active` covers scheduled and running objects; `paused` both Canvas pause modes. No status: both. */
    status: z.enum(CALENDAR_STATUSES).optional(),
  })
  .refine((q) => q.to >= q.from, { message: 'to is before from', path: ['to'] })
  .refine((q) => (Date.parse(q.to) - Date.parse(q.from)) / 86_400_000 < CALENDAR_MAX_DAYS, { message: `at most ${CALENDAR_MAX_DAYS} days`, path: ['to'] });
export type CalendarQuery = z.infer<typeof CalendarQuery>;

export interface CalendarItem {
  /** Unique within the response: `<objectType>:<objectId>:<runKey | window>`. */
  key: string;
  objectType: (typeof CALENDAR_OBJECT_TYPES)[number];
  objectId: string;
  name: string;
  /** The object's own status (campaign or Canvas). */
  status: string;
  deliveryType: (typeof CAMPAIGN_DELIVERY_TYPES)[number];
  channels: Array<(typeof MESSAGE_CHANNELS)[number]>;
  tags: Array<{ id: string; name: string; color: string | null }>;
  kind: 'scheduled_run' | 'active_window';
  /** Send day, or the first day of the window within the range (environment zone). */
  startDay: string;
  /** Same as `startDay` for a run; the last day of the window within the range. */
  endDay: string;
  /** `HH:mm` of a run: in the environment zone, or in each user's own zone with `localTime`. `null` for a window. */
  time: string | null;
  localTime: boolean;
  /** Run instant (reference zone) or window start; `end` is the window end, `null` when open-ended or a run. */
  start: string | null;
  end: string | null;
}

export interface CalendarOverlap {
  day: string;
  /** Item keys of the two runs. */
  a: string;
  b: string;
  channels: Array<(typeof MESSAGE_CHANNELS)[number]>;
}

export interface CalendarView {
  from: string;
  to: string;
  timeZone: string;
  items: CalendarItem[];
  overlaps: CalendarOverlap[];
}
